import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'

/**
 * A parent's "Create my account" link, opened into a Yellow Dot tab that is already sitting open
 * and signed out - rather than a second copy of the site in the tab the mail client just made.
 *
 * <p>Only a signed-out tab takes it. A signed-in one belongs to somebody - on a family computer,
 * usually the child who sent the invite - and pointing a signup at it would replace their session
 * with the parent's. That tab is left alone and the link carries on in its own tab, as before.
 *
 * <p>Same-origin tabs only: a BroadcastChannel never leaves this browser.
 */
const CHANNEL = 'yellowdot.invite-link'

/** Tells this tab's own messages apart from another tab's - a channel hears every tab, itself included. */
const TAB_ID = Math.random().toString(36).slice(2)

type InviteMessage =
  | { type: 'invite'; path: string; from: string }
  | { type: 'taken'; to: string; by: string }

/**
 * Offers the signup to a waiting signed-out tab. Resolves true if one took it, false - carry on
 * here - when none answers in time or the browser has no BroadcastChannel.
 */
export function offerInviteToOpenTab(path: string): Promise<boolean> {
  if (typeof BroadcastChannel === 'undefined') return Promise.resolve(false)
  return new Promise((resolve) => {
    const channel = new BroadcastChannel(CHANNEL)
    const done = (taken: boolean) => {
      window.clearTimeout(timer)
      channel.close()
      resolve(taken)
    }
    const timer = window.setTimeout(() => done(false), 1000)
    channel.onmessage = (e: MessageEvent<InviteMessage>) => {
      if (e.data?.type === 'taken' && e.data.to === TAB_ID) done(true)
    }
    channel.postMessage({ type: 'invite', path, from: TAB_ID } satisfies InviteMessage)
  })
}

/**
 * Mounted once at the root: listens for an invite link opened elsewhere and, if this tab is signed
 * out, opens the signup here.
 *
 * <p>The first tab to answer wins. Every other signed-out tab hears the same offer, so each one
 * waits to see whether another has already claimed it before navigating - otherwise two open tabs
 * would both jump to the signup.
 */
export function InviteHandoffListener() {
  const { token, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (loading || token || typeof BroadcastChannel === 'undefined') return
    const channel = new BroadcastChannel(CHANNEL)
    const claimed = new Set<string>()

    channel.onmessage = (e: MessageEvent<InviteMessage>) => {
      const msg = e.data
      if (msg?.type === 'taken') {
        // Another tab has it - stand down if we were about to.
        claimed.add(msg.to)
        return
      }
      if (msg?.type !== 'invite' || msg.from === TAB_ID) return
      // A short, random wait so that of several open tabs only the first claims it.
      window.setTimeout(() => {
        if (claimed.has(msg.from)) return
        channel.postMessage({ type: 'taken', to: msg.from, by: TAB_ID } satisfies InviteMessage)
        // Marked, so the register page it lands on does not offer it onward again.
        navigate(msg.path, { state: { inviteHandedOff: true } })
        window.focus()
      }, Math.random() * 150)
    }
    return () => channel.close()
  }, [token, loading, navigate])

  return null
}
